import { useState } from "react";
import { useSelector } from "react-redux";
import { Form, Row, Col, Button } from "react-bootstrap";
import DialogFooter from "./DialogFooter.js";
import { buildDashboardModule, buildDashboardPayload } from "./dashboardPayloadConstants.js";

const CreateFullkitView = ({
  initialSettings,
  onHide,
  isUpdateMode = false,
  reloadDashboard,
  fullkitCount,
  toastRef,
}) => {
  // Default options for FullkitStatus and From
  const [submitting, setSubmitting] = useState(false);
  const defaultFullkitStatusOptions = [
    { label: "Ready", value: "Ready", checked: false },
    { label: "Not Ready (Late)", value: "Not Ready (Late)", checked: false },
    { label: "Not Ready (On time)", value: "Not Ready (On time)", checked: false },
    { label: "In-Progress (On time)", value: "In-Progress (On time)", checked: false },
    { label: "In-Progress (Late)", value: "In-Progress (Late)", checked: false },
  ];
  const defaultFromOptions = [
    { label: "My Projects", value: "My Projects", checked: false },
    { label: "All Projects", value: "All Projects", checked: false },
  ];
  const defaultJobTypeOptions = [
    { label: "CIC/CICM", value: "CIC/CICM", checked: false },
    { label: "FA", value: "FA", checked: false },
  ];

  // Redux user info
  const { userInformation } = useSelector((state) => state.UserReducer);

  // Parse initialSettings values to arrays
  const initialFullkitStatus =
    initialSettings?.FullkitStatus?.split(",").map((f) => f.trim()) || [];
  const initialFrom = initialSettings?.From?.split(",").map((f) => f.trim()) || [];
  const initialJobType = initialSettings?.JobType?.split(",").map((j) => j.trim()) || [];

  const [fullkitStatusOptions, setFullkitStatusOptions] = useState(
    defaultFullkitStatusOptions.map((opt) => ({
      ...opt,
      checked: initialFullkitStatus.includes(opt.label),
    })),
  );
  const [fromOptions, setFromOptions] = useState(
    defaultFromOptions.map((opt) => ({
      ...opt,
      checked: initialFrom.includes(opt.label),
    })),
  );
  const [jobTypeOptions, setJobTypeOptions] = useState(
    defaultJobTypeOptions.map((opt) => ({
      ...opt,
      checked: initialJobType.includes(opt.label),
    })),
  );

  const handleCreateOrUpdate = async () => {
    setSubmitting(true);
    // Extract checked values for each option type
    const getChecked = (options, useLabel = false) =>
      options
        .filter((opt) => opt.checked)
        .map((opt) => (useLabel ? opt.label : opt.value))
        .join(",");

    const userId = initialSettings?.User_ID || userInformation?.userid;
    let moduleName;
    if (isUpdateMode) {
      moduleName = initialSettings?.Title;
    } else {
      const count = typeof fullkitCount === "number" && !isNaN(fullkitCount) ? fullkitCount : 0;
      moduleName = `Fullkit_${count + 1}`;
    }
    const module = buildDashboardModule({
      moduleId: isUpdateMode ? initialSettings?.Module_ID || "" : "",
      moduleName: moduleName,
      moduleType: "Fullkit",
      userId,
      config: {
        FullkitStatus: getChecked(fullkitStatusOptions, true),
        From: getChecked(fromOptions),
        JobType: getChecked(jobTypeOptions, true),
      },
      Sequence: isUpdateMode ? initialSettings?.Sequence : -1
    });
    const payload = buildDashboardPayload({ module });
    try {
      // Import createOrUpdateDashboardModuleView dynamically to avoid circular deps
      const { createOrUpdateDashboardModuleView } = await import("../../../apis/dashboardApi.js");
      await createOrUpdateDashboardModuleView(payload);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: isUpdateMode
            ? "Fullkit module updated successfully"
            : "Fullkit module created successfully",
          life: 3000,
        });
      }
      if (typeof reloadDashboard === "function") reloadDashboard();
      onHide();
    } catch (err) {
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to create/update Fullkit module.",
          life: 5000,
        });
      }
      console.error("Error creating/updating Fullkit module:", err);
      setSubmitting(false);
    }
  };

  const isFormValid =
    fromOptions.some((opt) => opt.checked) &&
    fullkitStatusOptions.some((opt) => opt.checked) &&
    jobTypeOptions.some((opt) => opt.checked);

  return (
    <>
      <Row className="fullkit-form">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="mb-2 module-form-label">
              From <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2  ">
              {fromOptions.map((option, idx) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`from-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => {
                    const updated = [...fromOptions];
                    updated[idx].checked = !updated[idx].checked;
                    setFromOptions(updated);
                  }}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="mb-2 module-form-label">
              Job Type <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2  ">
              {jobTypeOptions.map((option, idx) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`jobType-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => {
                    const updated = [...jobTypeOptions];
                    updated[idx].checked = !updated[idx].checked;
                    setJobTypeOptions(updated);
                  }}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="mb-2 module-form-label">
              Fullkit Status <span className="mandatory-asterisk"> *</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2  ">
              {fullkitStatusOptions.map((option, idx) => (
                <Form.Check
                  key={option.value}
                  type="checkbox"
                  id={`fullkitStatus-${option.value}`}
                  label={option.label}
                  checked={option.checked}
                  onChange={() => {
                    const updated = [...fullkitStatusOptions];
                    updated[idx].checked = !updated[idx].checked;
                    setFullkitStatusOptions(updated);
                  }}
                />
              ))}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <DialogFooter
        submitting={submitting}
        isFormValid={isFormValid}
        isUpdateMode={isUpdateMode}
        onCancel={onHide}
        onSubmit={handleCreateOrUpdate}
      />
    </>
  );
};

export default CreateFullkitView;
